import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from './context/AuthContext';
import { initiateSocket, getSocket } from './socket';
import axiosInstance from './axiosInstance';
import './styles/SafeOutToggle.css';

// Only these users can see this component
const AUTHORIZED_EMAILS = [
  'ericolsen@solu-m.com',
  'ericolsen@solumsp.com',
  'ericolsen@solum-group.com'
];

function SafeOutToggle() {
  const { user } = useContext(AuthContext);
  const [events, setEvents] = useState([]);
  const [currentStatus, setCurrentStatus] = useState('UNKNOWN');
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const [tags, setTags] = useState([]);

  const userEmail = user?.email?.toLowerCase() || '';
  const isAuthorized = AUTHORIZED_EMAILS.some(auth => userEmail === auth.toLowerCase());

  const webhookUrl = `${window.location.origin}/api/safe-out/receive`;

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await axiosInstance.get('/safe-out/status');
      if (data.success) {
        setCurrentStatus(data.currentStatus);
        setTags(data.tags || []);
      }
    } catch (err) {
      console.error('Failed to fetch status:', err);
      setError(err.response?.data?.message || 'Failed to load status');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/safe-out/history?limit=50');
      if (data.success) {
        setEvents(data.events || []);
      }
    } catch (err) {
      console.error('Failed to fetch history:', err);
    }
  }, []);

  const clearHistory = async () => {
    try {
      await axiosInstance.delete('/safe-out/history');
      setEvents([]);
    } catch (err) {
      console.error('Failed to clear history:', err);
    }
  };

  // WebSocket for real-time updates
  useEffect(() => {
    if (!isAuthorized) return;

    fetchStatus();
    fetchHistory();
    initiateSocket();
    const socket = getSocket();

    if (!socket) {
      console.warn('[SafeOut] Socket not available');
      return;
    }

    setConnected(socket.connected);

    const handleToggled = (data) => {
      console.log('[SafeOut] safeout:toggled', data);
      setEvents(prev => [data, ...prev].slice(0, 100));
      setCurrentStatus(data.newStatus);
    };

    const handleCleared = () => {
      setEvents([]);
    };

    socket.on('connect', () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));
    socket.on('safeout:toggled', handleToggled);
    socket.on('safeout:cleared', handleCleared);

    return () => {
      socket.off('connect');
      socket.off('disconnect');
      socket.off('safeout:toggled', handleToggled);
      socket.off('safeout:cleared', handleCleared);
    };
  }, [isAuthorized, fetchStatus, fetchHistory]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const formatTimestamp = (iso) => {
    const date = new Date(iso);
    return date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (!isAuthorized) {
    return null;
  }

  return (
    <div className="safe-out-monitor">
      <div className="safe-out-header">
        <h2>Safe/Out Toggle</h2>
        <div className="header-controls">
          <div className="safe-out-status">
            <span className={`status-dot ${connected ? 'connected' : 'disconnected'}`}></span>
            {connected ? 'Live' : 'Disconnected'}
          </div>
        </div>
      </div>

      <div className="safe-out-config">
        <div className="config-row">
          <label>Webhook URL:</label>
          <div className="url-display">
            <code>{webhookUrl}</code>
            <button
              className="copy-btn"
              onClick={() => copyToClipboard(webhookUrl)}
              title="Copy URL"
            >
              Copy
            </button>
          </div>
        </div>
        <p className="config-hint">
          Configure this URL in SOLUM SaaS Webhook Subscriptions for button press events on LOM store 001
        </p>
      </div>

      <div className="current-status-panel">
        <div className="status-label">Current Status:</div>
        <div className={`status-value ${currentStatus === 'OUT' ? 'out' : currentStatus === 'SAFE' ? 'safe' : ''}`}>
          {loading ? 'Loading...' : currentStatus}
        </div>
        {currentStatus === 'OUT' && <div className="blink-indicator">BLINKING</div>}
      </div>

      <div className="tags-info">
        <strong>Monitored Tags:</strong>
        {tags.map((tag, i) => (
          <span key={i} className="tag-badge">{tag.name}: {tag.labelCode}</span>
        ))}
      </div>

      {error && <div className="safe-out-error">{error}</div>}

      <div className="safe-out-controls">
        <button onClick={() => { fetchStatus(); fetchHistory(); }} className="refresh-btn">
          Refresh
        </button>
        <button onClick={clearHistory} className="clear-btn">
          Clear History
        </button>
      </div>

      <div className="safe-out-terminal">
        {events.length === 0 ? (
          <div className="terminal-empty">
            <span className="prompt">&gt;</span> Waiting for button press events...
            <span className="cursor">_</span>
          </div>
        ) : (
          events.map((event) => (
            <div
              key={event.id}
              className={`event-entry ${event.newStatus === 'OUT' ? 'out' : 'safe'}`}
            >
              <div className="event-summary">
                <span className="timestamp">[{formatTimestamp(event.timestamp)}]</span>
                <span className={`status-badge ${event.newStatus === 'OUT' ? 'out' : 'safe'}`}>
                  {event.previousStatus} → {event.newStatus}
                </span>
                <span className="tag-name">{event.tagName}</span>
                {event.blinkSent && (
                  <span className="blink-badge">BLINK 60min</span>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="safe-out-footer">
        <span>{events.length} event(s)</span>
      </div>

      <div className="safe-out-legend">
        <p>SAFE = Normal state (no blink)</p>
        <p>OUT = Alert state (RED blink for 60 minutes)</p>
        <p>Press physical button on tag to toggle between states</p>
      </div>
    </div>
  );
}

export default SafeOutToggle;
